"use server";

import {
  prisma,
  type Prisma,
  type Order,
  type OrderItem,
  type PricingRule,
  type Currency,
  PricingRuleTarget,
  PricingValueType,
} from "client";
import { buildPricingContext, calculatePricingWithContext } from "../../pricing-calculator";
import type { AppliedPricingRulesPayload } from "../order-v2/types";

export type PricingCalculationResult = ReturnType<
  typeof calculatePricingWithContext
>;
/**
 * Fetch and validate pricing rules from the database
 */
async function fetchAndValidatePricingRules(
  appliedPricingRules: AppliedPricingRulesPayload,
  eventId: string,
  tx?: Prisma.TransactionClient
): Promise<PricingRule[]> {
  const db = tx ?? prisma;

  const allAppliedRulesIds = [
    ...appliedPricingRules.autoAppliedRuleIds,
    ...appliedPricingRules.promoCodeRuleIds,
    ...appliedPricingRules.paymentMethodRuleIds,
  ];

  if (allAppliedRulesIds.length === 0) {
    return [];
  }

  const rules = await db.pricingRule.findMany({
    where: {
      id: { in: allAppliedRulesIds },
      eventId,
      deletedAt: null,
      stoppedAt: null,
      startDate: { lte: new Date() },
      endDate: { gte: new Date() },
    },
  });

  // Validate all requested rules were found and are active
  if (rules.length !== allAppliedRulesIds.length) {
    const foundIds = rules.map((r) => r.id);
    const missingIds = allAppliedRulesIds.filter(
      (id) => !foundIds.includes(id)
    );
    throw new Error(
      `Invalid or inactive pricing rules: ${missingIds.join(", ")}`
    );
  }

  return rules;
}

/**
 * Group pricing rules by target for calculation engine
 */
function groupPricingRulesByTarget(rules: PricingRule[]): {
  orderItemRules: PricingRule[];
  orderRules: PricingRule[];
  paymentRules: PricingRule[];
} {
  const grouped: {
    orderItemRules: PricingRule[];
    orderRules: PricingRule[];
    paymentRules: PricingRule[];
  } = {
    orderItemRules: [],
    orderRules: [],
    paymentRules: [],
  };

  for (const rule of rules) {
    if (rule.target === PricingRuleTarget.ORDER_ITEM) {
      grouped.orderItemRules.push(rule);
    } else if (rule.target === PricingRuleTarget.ORDER) {
      grouped.orderRules.push(rule);
    } else if (rule.target === PricingRuleTarget.PAYMENT) {
      grouped.paymentRules.push(rule);
    }
  }

  return grouped;
}

/**
 * Helper to create breakdown from applied rule
 */
function createBreakdown(rule: {
  ruleId: string;
  previewLabel: string;
  totalApplied: number;
  flatApplied: number;
  percentApplied: number;
  taxApplied: number;
}) {
  return [
    {
      label: rule.previewLabel,
      valueType:
        rule.flatApplied > 0 && rule.percentApplied > 0
          ? PricingValueType.FLAT // Primary type when both exist
          : rule.flatApplied > 0
          ? PricingValueType.FLAT
          : PricingValueType.PERCENT,
      flatValue: rule.flatApplied > 0 ? rule.flatApplied : null,
      percentageValue:
        rule.percentApplied > 0 ? Math.abs(rule.percentApplied / 100) : null,
      appliedValue: rule.totalApplied,
    },
  ];
}

/**
 * Serialize a PricingRule object to JSON-compatible format
 */
function serializeRuleForSnapshot(rule: PricingRule) {
  return {
    ...rule,
    startDate: rule.startDate.toISOString(),
    endDate: rule.endDate.toISOString(),
    stoppedAt: rule.stoppedAt?.toISOString() || null,
    deletedAt: rule.deletedAt?.toISOString() || null,
    createdAt: rule.createdAt.toISOString(),
    updatedAt: rule.updatedAt.toISOString(),
  };
}

/**
 * Create audit records for applied pricing rules
 */
function createAuditRecords(
  orderId: string,
  eventId: string,
  calculationResult: PricingCalculationResult,
  orderItems: OrderItem[],
  appliedRulesMap: Map<string, PricingRule>,
  selectedPaymentMethodId: string | undefined,
  currency: Currency
): Prisma.AppliedPricingAdjustmentCreateManyInput[] {
  const auditRecords: Prisma.AppliedPricingAdjustmentCreateManyInput[] = [];

  // Process item-level rules
  // Match by ticketId or entityId
  for (const itemResult of calculationResult.itemResults) {
    const orderItem = orderItems.find(
      (oi) =>
        oi.ticketId === itemResult.itemId || oi.entityId === itemResult.itemId
    );

    if (!orderItem) continue;

    const allItemRules = [
      ...itemResult.discountBreakdown,
      ...itemResult.chargeBreakdown,
    ];

    for (const appliedRule of allItemRules) {
      const fullRule = appliedRulesMap.get(appliedRule.ruleId);
      auditRecords.push({
        target: PricingRuleTarget.ORDER_ITEM,
        amount: appliedRule.totalApplied,
        currency,
        taxable: appliedRule.taxApplied > 0,
        breakdown: createBreakdown(appliedRule),
        ruleSnapshot: fullRule
          ? serializeRuleForSnapshot(fullRule)
          : {
              ruleId: appliedRule.ruleId,
              previewLabel: appliedRule.previewLabel,
              totalApplied: appliedRule.totalApplied,
            },
        eventId,
        pricingRuleId: appliedRule.ruleId,
        orderId,
        orderItemId: orderItem.id,
      });
    }
  }

  // Process order-level rules
  const orderResult = calculationResult.orderResult;
  const allOrderRules = [
    ...orderResult.discountBreakdown,
    ...orderResult.chargeBreakdown,
  ];

  for (const appliedRule of allOrderRules) {
    const fullRule = appliedRulesMap.get(appliedRule.ruleId);
    auditRecords.push({
      target: PricingRuleTarget.ORDER,
      amount: appliedRule.totalApplied,
      currency,
      taxable: appliedRule.taxApplied > 0,
      breakdown: createBreakdown(appliedRule),
      ruleSnapshot: fullRule
        ? serializeRuleForSnapshot(fullRule)
        : {
            ruleId: appliedRule.ruleId,
            previewLabel: appliedRule.previewLabel,
            totalApplied: appliedRule.totalApplied,
          },
      eventId,
      pricingRuleId: appliedRule.ruleId,
      orderId,
    });
  }

  // Process payment-level rules
  if (selectedPaymentMethodId) {
    const paymentResult = calculationResult.paymentResult;
    for (const appliedRule of paymentResult.chargeBreakdown) {
      const fullRule = appliedRulesMap.get(appliedRule.ruleId);
      auditRecords.push({
        target: PricingRuleTarget.PAYMENT,
        amount: appliedRule.totalApplied,
        currency,
        taxable: appliedRule.taxApplied > 0,
        breakdown: createBreakdown(appliedRule),
        ruleSnapshot: fullRule
          ? serializeRuleForSnapshot(fullRule)
          : {
              ruleId: appliedRule.ruleId,
              previewLabel: appliedRule.previewLabel,
              totalApplied: appliedRule.totalApplied,
            },
        eventId,
        pricingRuleId: appliedRule.ruleId,
        orderId,
        paymentId: selectedPaymentMethodId,
      });
    }
  }

  return auditRecords;
}

/**
 * Process pricing rules and create audits for order completion in updateOrderV2
 *
 * This is the main service used by updateOrderV2 to:
 * 1. Fetch and validate pricing rules (single DB query)
 * 2. Calculate pricing using the engine
 * 3. Create audit records
 * 4. Return calculated totals for order and items
 *
 * @returns Object containing calculation results and audit creation function
 */
export async function processPricingRulesForOrder(
  order: Order & { orderItems: OrderItem[] },
  appliedPricingRules: AppliedPricingRulesPayload,
  selectedPaymentMethodId: string | undefined,
  taxRate: number,
  tx?: Prisma.TransactionClient
): Promise<{
  calculationResult: PricingCalculationResult;
  createAudits: () => Promise<void>;
}> {
  const db = tx ?? prisma;

  try {
    // 1. Fetch and validate rules (SINGLE DB QUERY)
    const rules = await fetchAndValidatePricingRules(
      appliedPricingRules,
      order.eventId,
      db
    );

    // Create rules map for audit creation
    const appliedRulesMap = new Map<string, PricingRule>();
    rules.forEach((rule) => appliedRulesMap.set(rule.id, rule));

    // 2. Build pricing context from order
    const pricingContext = buildPricingContext(order, selectedPaymentMethodId);

    // 3. Group rules by target
    const rulesToApply = groupPricingRulesByTarget(rules);

    // 4. Calculate pricing using engine
    const calculationResult = calculatePricingWithContext({
      pricingContext,
      rulesToApply,
      taxRate,
    });

    // 5. Return results with audit creation function
    return {
      calculationResult,
      createAudits: async () => {
        const auditRecords = createAuditRecords(
          order.id,
          order.eventId,
          calculationResult,
          order.orderItems,
          appliedRulesMap,
          selectedPaymentMethodId,
          order.currency
        );

        if (auditRecords.length > 0) {
          await db.appliedPricingAdjustment.createMany({
            data: auditRecords,
          });
        }
      },
    };
  } catch (error) {
    console.error("Failed to process pricing rules:", error);
    throw error;
  }
}
